import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { formatTokenAmount } from '../config/token';
import RepairFeedbackBanner from './RepairFeedbackBanner';
import { walletService } from '../services/walletService';

interface Transaction {
  id: string;
  transactionId: string;
  type: string;
  status: string;
  amount: number;
  signature: string | null;
  errorMessage: string | null;
  fromWalletId: string;
  toWalletId: string;
  metadata: any;
  createdAt: string;
  updatedAt: string;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

export const FailedTransactions: React.FC = () => {
  const [failedTransactions, setFailedTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryingTransaction, setRetryingTransaction] = useState<string | null>(null);
  const [repairingTransaction, setRepairingTransaction] = useState<string | null>(null);
  const [repairFeedback, setRepairFeedback] = useState<string | null>(null);

  useEffect(() => {
    loadFailedTransactions();
  }, []);

  const loadFailedTransactions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/transaction/filter/search?status=failed&page=1&limit=100`);
      // The filter endpoint returns a paginated `{ data, meta }` shape.
      const data = response.data?.data ?? response.data;
      setFailedTransactions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load failed transactions');
      console.error('Error loading failed transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRetryTransaction = async (transactionId: string) => {
    try {
      setRetryingTransaction(transactionId);
      await axios.post(`${API_BASE_URL}/transaction/${transactionId}/retry`);
      await loadFailedTransactions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to retry transaction');
    } finally {
      setRetryingTransaction(null);
    }
  };

  const handleRepairWallets = async (transaction: Transaction) => {
    const key = transaction.transactionId;
    try {
      setRepairingTransaction(key);
      setError('');
      setRepairFeedback(null);
      const results = await walletService.repairTransactionWallets(
        transaction.fromWalletId,
        transaction.toWalletId
      );
      setRepairFeedback(walletService.summarizeRepairs(results));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to repair wallets');
    } finally {
      setRepairingTransaction(null);
    }
  };

  const handleRepairAndRetry = async (transaction: Transaction) => {
    const key = transaction.transactionId;
    try {
      setRepairingTransaction(key);
      setRetryingTransaction(key);
      setError('');
      setRepairFeedback(null);
      const results = await walletService.repairTransactionWallets(
        transaction.fromWalletId,
        transaction.toWalletId
      );
      await axios.post(`${API_BASE_URL}/transaction/${transaction.transactionId}/retry`);
      await loadFailedTransactions();
      setRepairFeedback(`${walletService.summarizeRepairs(results)} Transaction retry submitted.`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to repair and retry transaction');
    } finally {
      setRepairingTransaction(null);
      setRetryingTransaction(null);
    }
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

  const truncateAddress = (address: string) => `${address.slice(0, 8)}...${address.slice(-8)}`;

  if (loading) {
    return (
      <div className="w-full p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto"></div>
          <p className="mt-4 text-slate-400">Loading failed transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-slate-100">Failed Transactions</h2>
        <button
          onClick={loadFailedTransactions}
          className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-red-500"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 rounded-md border border-red-500/30 bg-red-500/10 text-red-300">
          {error}
        </div>
      )}

      {repairFeedback && (
        <div className="mb-4">
          <RepairFeedbackBanner message={repairFeedback} onDismiss={() => setRepairFeedback(null)} />
        </div>
      )}

      <div className="rounded-xl border border-slate-700 bg-slate-800 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-slate-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Transaction ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">From</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">To</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Error Message</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {failedTransactions.map((transaction) => (
                <tr key={transaction.id} className="hover:bg-slate-700/40 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono text-slate-200">
                      {truncateAddress(transaction.transactionId)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-500/15 text-red-300 ring-1 ring-inset ring-red-500/30">
                      {transaction.type.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-200 font-mono">
                      {formatTokenAmount(transaction.amount)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono text-slate-200">
                      {transaction.fromWalletId ? truncateAddress(transaction.fromWalletId) : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono text-slate-200">
                      {transaction.toWalletId ? truncateAddress(transaction.toWalletId) : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-red-400 max-w-xs truncate" title={transaction.errorMessage || ''}>
                      {transaction.errorMessage || 'Unknown error'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                    {formatDate(transaction.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => handleRepairAndRetry(transaction)}
                        disabled={
                          repairingTransaction === transaction.transactionId ||
                          retryingTransaction === transaction.transactionId
                        }
                        className="text-left text-indigo-400 hover:text-indigo-300 disabled:text-slate-500 font-medium"
                      >
                        {repairingTransaction === transaction.transactionId ||
                        retryingTransaction === transaction.transactionId
                          ? 'Working...'
                          : 'Repair & Retry'}
                      </button>
                      <button
                        onClick={() => handleRepairWallets(transaction)}
                        disabled={repairingTransaction === transaction.transactionId}
                        className="text-left text-slate-400 hover:text-slate-300 disabled:text-slate-500 inline-flex items-center gap-1"
                      >
                        <WrenchScrewdriverIcon className="h-3.5 w-3.5" />
                        Repair wallets
                      </button>
                      <button
                        onClick={() => handleRetryTransaction(transaction.transactionId)}
                        disabled={retryingTransaction === transaction.transactionId}
                        className="text-left text-red-400 hover:text-red-300 disabled:text-slate-500 font-medium"
                      >
                        {retryingTransaction === transaction.transactionId ? 'Retrying...' : 'Retry only'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {failedTransactions.length === 0 && (
          <div className="text-center py-8">
            <p className="text-slate-400">No failed transactions found.</p>
          </div>
        )}
      </div>

      {failedTransactions.length > 0 && (
        <div className="mt-6 text-sm text-slate-400">
          <p>Total Failed Transactions: {failedTransactions.length}</p>
        </div>
      )}
    </div>
  );
};
